import 'whatwg-fetch';

import filter from 'lodash/filter';

/**
 * Parses the response data to JSON and handle any invalid response.
 *
 * @private
 * @param {Object} response The HTTP response.
 * @returns {Promise<Object>}
 */
function parse(response) {
  if (response.status >= 200 && response.status < 300) {
    return response.json();
  }

  const error = new Error(response.statusText);

  error.response = response;

  throw error;
}

export default {
  /**
   * Returns checkout cart details.
   *
   * @param {String} origin The VTEX API base URL.
   * @returns {Promise}
   */
  getCart(origin) {
    const query = `
      query {
        cart: orderForm @context(provider: "vtex.store-graphql") {
          id: orderFormId
          preferences: storePreferencesData {
            country: countryCode
            currency: currencyCode
          }
          items {
            sku: id
            ean
            name
            price
            quantity
            imageUrl
          }
        }
      }
    `;

    return fetch(origin, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: {},
      }),
    })
      .then(parse)
      .then(({ data }) => data.cart);
  },
  /**
   * Returns a specified checkout cart details.
   *
   * @param {String} origin The VTEX API base URL.
   * @param {String} id Cart ID.
   * @returns {Promise}
   */
  getCartByID(origin, id) {
    const query = `
      query($orderFormId: String!) {
        cart: searchOrderForm(orderFormId: $orderFormId) @context(provider: "vtex.store-graphql") {
          id: orderFormId
          preferences: storePreferencesData {
            country: countryCode
            currency: currencyCode
          }
          items {
            sku: id
            ean
            name
            price
            quantity
            imageUrl
          }
        }
      }
    `;

    return fetch(origin, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: {
          orderFormId: id,
        },
      }),
    })
      .then(parse)
      .then(({ data }) => data.cart);
  },
  /**
   * Add a product to the order form.
   *
   * @param {String} origin The VTEX API base URL.
   * @param {String} id Cart ID.
   * @param {Object} item Product item.
   * @returns {Promise<Object>}
   */
  addToCart(origin, id, item) {
    const query = `
      mutation($orderFormId: ID!, $items: [ItemInput]!) {
        cart: addToCart(orderFormId: $orderFormId, items: $items) @context(provider: "vtex.checkout-graphql") {
          id
        }
      }
    `;

    return fetch(origin, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: {
          orderFormId: id,
          items: [item],
        },
      }),
    })
      .then(parse)
      .then(({ data }) => data.cart);
  },
  /**
   * Remove a product from the order form.
   *
   * @param {String} origin The VTEX API base URL.
   * @param {String} id Cart ID.
   * @param {Object} item Product item.
   * @returns {Promise<Object>}
   */
  removeFromCart(origin, id, item) {
    const query = `
      mutation($orderFormId: ID!, $orderItems: [ItemInput]) {
        cart: updateItems(orderFormId: $orderFormId, orderItems: $orderItems) @context(provider: "vtex.checkout-graphql") {
          id
        }
      }
    `;

    return fetch(origin, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: {
          orderFormId: id,
          orderItems: [
            {
              id: item.id,
              index: item.index,
              quantity: 0,
            },
          ],
        },
      }),
    })
      .then(parse)
      .then(({ data }) => data.cart);
  },
  /**
   * Returns a list of products matching the list of EAN.
   *
   * @param {String} origin The VTEX API base URL.
   * @param {Array<String>} eans The list of product EAN.
   * @returns {Promise<Array<Object>}
   */
  getProductsByEAN(origin, eans) {
    const query = `
      query($eans: [ID!]) {
        products: productsByIdentifier(field: ean, values: $eans) @context(provider: "vtex.store-graphql") {
          name: productName
          brand
          categories
          items {
            sku: itemId
            ean
            sellers {
              id: sellerId
              default: sellerDefault
              offer: commertialOffer {
                currentPrice: Price
                listPrice: ListPrice
                quantity: AvailableQuantity
                installments: Installments {
                  total: NumberOfInstallments
                  value: Value
                }
              }
            }
          }
        }
      }
    `;

    return fetch(origin, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query,
        variables: {
          eans: filter(eans, (ean) => !!ean.match(/^\d+$/gi)),
        },
      }),
    })
      .then(parse)
      .then(({ data }) => data.products || []);
  },
  getUserName(origin, token) {
    return fetch(origin, {
      method: 'GET',
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then(parse);
  },
};
